import { staleTimes, useQuery } from '@platform/api-client';
import type { TypedClient } from '@platform/openapi';
import type { OperationWindow } from './list/operation-period.ts';
import { queryKeys, usePageContext } from '../usePageContext.ts';
import { fetchAllPages, referencePageParams } from '../useKeysetPages.ts';

export const useCategories = (api: TypedClient, status: 'active' | 'archived') => {
  const { tenantId } = usePageContext();
  return useQuery({
    queryKey: queryKeys.categories(tenantId, { status, every: true }),
    queryFn: () =>
      fetchAllPages((pageToken) =>
        api.get('/admin/categories', { query: { status, ...referencePageParams(pageToken) } }),
      ),
    staleTime: staleTimes.catalog,
  });
};

export const useActiveCategories = (api: TypedClient) => useCategories(api, 'active');

export const useCategoryProductCounts = (api: TypedClient) => {
  const { tenantId } = usePageContext();
  return useQuery({
    queryKey: queryKeys.categories(tenantId, { productCounts: true }),
    queryFn: () => api.get('/admin/categories/product-counts', {}),
    staleTime: staleTimes.catalog,
    select: (data) => new Map(data.counts.map((entry) => [entry.categoryId, entry.products])),
  });
};

export const useTenantDetail = (api: TypedClient) => {
  const { tenantId } = usePageContext();
  return useQuery({
    queryKey: queryKeys.tenant(tenantId),
    queryFn: () => api.get('/admin/tenant', {}),
    staleTime: staleTimes.settings,
  });
};

export const useProduct = (api: TypedClient, productId: string | null) => {
  const { tenantId } = usePageContext();
  return useQuery({
    queryKey: queryKeys.product(tenantId, productId ?? ''),
    queryFn: () => api.get('/admin/products/{id}', { path: { id: productId ?? '' } }),
    enabled: productId !== null,
    staleTime: staleTimes.catalog,
  });
};

export const useOptions = (api: TypedClient) => {
  const { tenantId } = usePageContext();
  return useQuery({
    queryKey: queryKeys.options(tenantId),
    queryFn: () => api.get('/admin/options', {}),
    staleTime: staleTimes.catalog,
  });
};

export type CountScope = {
  readonly company: string | null;
  readonly operationWindow: OperationWindow | null;
};

export const useProductCounts = (api: TypedClient, { company, operationWindow }: CountScope) => {
  const { tenantId } = usePageContext();
  return useQuery({
    queryKey: queryKeys.products(tenantId, { view: 'counts', company, operationWindow }),
    queryFn: () =>
      api.get('/admin/products/counts', {
        query: {
          ...(company === null ? {} : { company }),
          ...(operationWindow === null
            ? {}
            : {
                operation_since: operationWindow.since,
                ...(operationWindow.until === null ? {} : { operation_until: operationWindow.until }),
              }),
        },
      }),
    staleTime: staleTimes.catalog,
  });
};
